import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Palette, Copy, ArrowRightLeft, Loader2 } from 'lucide-react';
import { API_BASE } from '../../config/api';
import { CRICKET_CARD_COVER_PRESETS } from '../../utils/cricketCardUtils';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/** Impede o @hello-pangea/dnd de capturar o evento no document. */
function stopBubbleToDoc(e) {
  e.stopPropagation();
}

/**
 * Menu flutuante (cor, copiar, mover). «Abrir cartão» fica no próprio cartão.
 * @param {{ top: number, left: number }} props.anchor
 */
export default function CricketCardActionsMenu({
  open,
  anchor,
  onClose,
  readOnly,
  layoutId,
  authToken,
  card,
  listId,
  sortedLists,
  onOpenMove,
  onAfterMutation,
  showToast,
  persistCardPatch,
  duplicateCardLocal,
}) {
  const { t } = useTranslation(['cricket']);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [colorBusy, setColorBusy] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);

  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const w = 224;
    const left = Math.max(8, Math.min(anchor.left, (typeof window !== 'undefined' ? window.innerWidth : 9999) - w - 8));
    const top = Math.max(8, anchor.top);
    setPos({ top, left });
  }, [open, anchor]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const notify = (msg, type) => {
    if (typeof showToast === 'function') showToast(msg, type || 'warning');
  };

  const patchColor = async (hex) => {
    if (readOnly || !card?.id) return;
    if (!persistCardPatch && !authToken) return;
    setColorBusy(true);
    try {
      const body = hex == null || hex === '' ? { cover_color: null } : { cover_color: hex };
      if (typeof persistCardPatch === 'function') {
        const ok = await persistCardPatch(card.id, body);
        if (!ok) {
          notify(t('cricket:syncError'), 'error');
          return;
        }
      } else {
        const res = await fetch(
          `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(card.id)}`,
          { method: 'PATCH', headers: authHeaders(authToken), body: JSON.stringify(body) },
        );
        if (!res.ok) {
          notify(t('cricket:syncError'), 'error');
          return;
        }
      }
      onClose();
      if (typeof onAfterMutation === 'function') await onAfterMutation();
    } catch {
      notify(t('cricket:syncError'), 'error');
    } finally {
      setColorBusy(false);
    }
  };

  const duplicate = async () => {
    if (readOnly || !card?.id) return;
    if (!duplicateCardLocal && !authToken) return;
    setCopyBusy(true);
    try {
      if (typeof duplicateCardLocal === 'function') {
        const ok = await duplicateCardLocal(card.id, listId);
        if (!ok) {
          notify(t('cricket:copyCardError'), 'error');
          return;
        }
      } else {
        const res = await fetch(
          `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(card.id)}/duplicate`,
          { method: 'POST', headers: authHeaders(authToken), body: JSON.stringify({ list_id: listId }) },
        );
        if (!res.ok) {
          notify(t('cricket:copyCardError'), 'error');
          return;
        }
      }
      notify(t('cricket:copyCardSuccess'), 'success');
      onClose();
      if (typeof onAfterMutation === 'function') await onAfterMutation();
    } catch {
      notify(t('cricket:copyCardError'), 'error');
    } finally {
      setCopyBusy(false);
    }
  };

  if (!open || !card) return null;

  const otherLists = (sortedLists || []).filter((l) => l.id !== listId);

  return createPortal(
    <div data-cricket-actions-root className="pointer-events-none fixed inset-0 z-[20000]">
      <button
        type="button"
        className="pointer-events-auto absolute inset-0 cursor-default bg-black/20"
        aria-label={t('cricket:closeActionsMenu')}
        onMouseDown={stopBubbleToDoc}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div
        data-cricket-more-menu
        className="pointer-events-auto absolute w-56 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-600 dark:bg-slate-900"
        style={{ top: pos.top, left: pos.left, zIndex: 1 }}
        role="menu"
        onMouseDown={stopBubbleToDoc}
        onClick={stopBubbleToDoc}
      >
        <div className="border-b border-slate-100 px-2 py-2 dark:border-slate-800">
          <div className="mb-1.5 flex items-center gap-2 px-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
            <Palette size={14} aria-hidden />
            {t('cricket:changeCardColor')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              disabled={readOnly || colorBusy}
              title={t('cricket:clearCardColor')}
              className="h-7 w-7 shrink-0 rounded border-2 border-slate-300 bg-white text-[10px] font-bold text-slate-500 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
              onMouseDown={stopBubbleToDoc}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                patchColor(null);
              }}
            >
              ∅
            </button>
            {CRICKET_CARD_COVER_PRESETS.map((hex) => (
              <button
                key={hex}
                type="button"
                disabled={readOnly || colorBusy}
                className="h-7 w-7 shrink-0 rounded border border-black/10 shadow-sm disabled:opacity-50"
                style={{ backgroundColor: hex }}
                title={hex}
                onMouseDown={stopBubbleToDoc}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  patchColor(hex);
                }}
              />
            ))}
          </div>
          {colorBusy && (
            <div className="mt-2 flex justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" aria-hidden />
            </div>
          )}
        </div>

        <button
          type="button"
          role="menuitem"
          disabled={readOnly || copyBusy}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-100 dark:hover:bg-slate-800"
          onMouseDown={stopBubbleToDoc}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            duplicate();
          }}
        >
          {copyBusy ? (
            <Loader2 size={16} className="shrink-0 animate-spin text-slate-500" aria-hidden />
          ) : (
            <Copy size={16} className="shrink-0 text-slate-500" aria-hidden />
          )}
          {t('cricket:copyCard')}
        </button>

        {otherLists.length > 0 && (
          <button
            type="button"
            role="menuitem"
            disabled={readOnly}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-100 dark:hover:bg-slate-800"
            onMouseDown={stopBubbleToDoc}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenMove();
              onClose();
            }}
          >
            <ArrowRightLeft size={16} className="shrink-0 text-slate-500" aria-hidden />
            {t('cricket:moveCard')}
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
